import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "./config";
import "./ResetPassword.css";

function isMobileBrowser() {
  return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}

function ResetPassword() {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [deepLinkAttempted, setDeepLinkAttempted] = useState(false);

  useEffect(() => {
    if (!isMobileBrowser() || !token) return;

    // Try to open the app via deep link; fall back to web form after 2s
    const deepLink = `photentra://reset-password/${token}`;
    window.location.href = deepLink;

    const fallbackTimer = setTimeout(() => {
      setDeepLinkAttempted(true);
    }, 2000);

    return () => clearTimeout(fallbackTimer);
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Something went wrong");
      } else {
        setMessage(data.message);
        setSuccess(true);
      }
    } catch (err) {
      setError("Error connecting to server");
    } finally {
      setLoading(false);
    }
  };

  // On mobile, show a brief "opening app" message while the deep link fires
  if (isMobileBrowser() && !deepLinkAttempted && !success) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-box">
          <a href="/" className="rp-logo-link">
            <img src={process.env.PUBLIC_URL + "/photentra-logo.png"} alt="Photentra" className="rp-logo" />
          </a>
          <p className="reset-password-description">
            Opening the Photentra app&hellip;
          </p>
          <p style={{ fontSize: "13px", color: "#64748b", marginTop: "12px" }}>
            If the app does not open, the form will load automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-box">
        <h1>Reset Password</h1>
        {!success ? (
          <>
            <p className="reset-password-description">
              Enter your new password below.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter new password"
                  minLength={6}
                />
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm new password"
                  minLength={6}
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </>
        ) : (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <p>{message}</p>
            <a href="/login" className="login-button">Go to Login</a>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
